import React, { useEffect, useRef, useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { justoAPI } from '@/lib/justo';
import { recuentosAPI, downloadBlob, formatFechaHora, errorMsg } from '@/lib/recuentos';

const SIN_DESTINO = 'none';

const PlantillaTransferencia = ({ cfg, onChange }) => {
  const ref = useRef(null);
  const subir = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    try { await justoAPI.subirPlantilla(file); toast.success('Plantilla reemplazada'); onChange(); }
    catch (err) { toast.error(errorMsg(err, 'Error al subir')); }
  };
  const descargar = async () => downloadBlob((await justoAPI.descargarPlantilla()).data, 'plantilla_transferencia_justo.xlsx');
  return (
    <div className="bg-white rounded-xl border p-4 space-y-2" data-testid="justo-plantilla-card">
      <h3 className="font-semibold text-slate-800">Plantilla de transferencia Justo</h3>
      <p className="text-sm text-slate-600">{cfg.plantilla.filename}{cfg.plantilla.actualizada ? ` · reemplazada ${formatFechaHora(cfg.plantilla.actualizada)}` : ''} · hojas: {cfg.plantilla.hojas.join(', ')}</p>
      <p className="text-xs text-slate-500">Se llena solo la hoja "Transferencia"; la hoja "Ingredientes" queda tal cual.</p>
      <div className="flex gap-2">
        <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={subir} data-testid="justo-plantilla-input" />
        <Button variant="outline" onClick={() => ref.current?.click()} data-testid="justo-plantilla-subir-btn"><Upload className="w-4 h-4 mr-1" /> Reemplazar</Button>
        <Button variant="outline" onClick={descargar} data-testid="justo-plantilla-descargar-btn"><Download className="w-4 h-4 mr-1" /> Descargar</Button>
      </div>
    </div>
  );
};

export const DestinosTab = () => {
  const [destinos, setDestinos] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [cfg, setCfg] = useState(null);

  const load = () => Promise.all([justoAPI.destinos(), recuentosAPI.lugares(), justoAPI.config()])
    .then(([d, l, c]) => { setDestinos(d.data); setLugares(l.data); setCfg(c.data); });
  useEffect(() => { load(); }, []);

  const save = async (locId, v) => {
    try { await justoAPI.updateDestino(locId, v === SIN_DESTINO ? null : v); toast.success('Destino guardado'); load(); }
    catch (e) { toast.error(errorMsg(e, 'Error')); }
  };
  const toggleOrigen = async (id, checked) => {
    const ids = checked ? [...cfg.origen_warehouse_ids, id] : cfg.origen_warehouse_ids.filter((x) => x !== id);
    try { await justoAPI.updateConfig({ origen_warehouse_ids: ids }); toast.success('Bodegas de origen guardadas'); load(); }
    catch (e) { toast.error(errorMsg(e, 'Error')); }
  };
  const guardarTodo = async () => {
    try { const { data } = await justoAPI.guardarSugerencias(); toast.success(data.guardados ? `${data.guardados} asignaciones guardadas` : 'Todas las asignaciones ya estaban guardadas'); load(); }
    catch (e) { toast.error(errorMsg(e, 'Error')); }
  };
  const pendientes = destinos.filter((d) => !d.configurado).length;

  if (!cfg) return <p className="mt-4 text-slate-500">Cargando...</p>;
  return (
    <div className="space-y-6 mt-4">
      <div className="bg-white rounded-xl border p-4 space-y-2" data-testid="justo-origenes">
        <p className="text-sm text-slate-600">Bodegas TUPU cuyos envíos salen en Justo desde <b>Grecia / Bodega Central</b> (puedes marcar más de una):</p>
        <div className="flex flex-wrap gap-4">
          {cfg.warehouses.map((w) => (
            <label key={w.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={cfg.origen_warehouse_ids.includes(w.id)} onChange={(e) => toggleOrigen(w.id, e.target.checked)}
                className="w-4 h-4 accent-orange-500" data-testid={`justo-origen-${w.id}`} /> {w.name}
            </label>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={guardarTodo} className="bg-orange-500 hover:bg-orange-600" data-testid="justo-guardar-asignaciones-btn">Guardar asignaciones</Button>
        <span className="text-xs text-slate-500" data-testid="justo-asignaciones-pendientes">{pendientes ? `${pendientes} locales con sugerencia sin guardar` : 'Todas las asignaciones están guardadas'}</span>
      </div>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm" data-testid="justo-destinos-table">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="px-4 py-2 text-left">Local TUPU</th><th className="px-4 py-2 text-left">Bodega TUPU que lo abastece</th><th className="px-4 py-2 text-left">Destino en Justo</th><th className="px-4 py-2 text-left" />
          </tr></thead>
          <tbody>
            {destinos.map((d) => (
              <tr key={d.id} className="border-t" data-testid={`justo-destino-row-${d.id}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{d.name}</td>
                <td className="px-4 py-2 text-xs" data-testid={`justo-destino-bodegas-${d.id}`}>
                  {d.bodegas_tupu.length === 0 && <span className="text-slate-400">—</span>}
                  {d.bodegas_tupu.map((w) => (
                    <span key={w.id} className={`inline-block mr-1 mb-0.5 px-2 py-0.5 rounded-full ${w.es_origen ? 'bg-green-100 text-green-700' : d.lugar_codigo ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}
                      title={w.es_origen ? 'Es bodega de origen' : 'No está marcada como origen: sus envíos no se exportan'}>{w.name}{!w.es_origen && d.lugar_codigo ? ' ⚠' : ''}</span>
                  ))}
                </td>
                <td className="px-4 py-2">
                  <select value={d.lugar_codigo || SIN_DESTINO} onChange={(e) => save(d.id, e.target.value)}
                    className={`h-9 rounded-md border px-2 text-sm ${d.lugar_codigo ? '' : 'text-slate-400'}`} data-testid={`justo-destino-select-${d.id}`}>
                    <option value={SIN_DESTINO}>No se exporta a Justo</option>
                    {lugares.filter((l) => l.codigo !== 'grecia_central').map((l) => <option key={l.codigo} value={l.codigo}>{l.local} / {l.nombre_bodega_justo}</option>)}
                  </select>
                </td>
                <td className="px-4 py-2 text-xs">{d.configurado ? <span className="text-green-600">Guardado</span> : <span className="text-amber-600">Sugerido, sin guardar</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PlantillaTransferencia cfg={cfg} onChange={load} />
    </div>
  );
};
